/**
 * ViewSwitch — the Flow ⇄ Books switch, in the HEADER (wv-5; the names are the
 * founder's pick, wv-6).
 *
 * The founder's spec (2026-08-06): the switch lives in the top bar beside the
 * settings gear — not buried in the profile menu — and hovering it tells you
 * which view you're in and what actually differs. Two states are a switch,
 * not a menu: clicking flips straight to the other view, and a toast names
 * where you landed — so on a phone, where hover does not exist
 * (`ui-ux-conventions`), the flip still explains itself. Since viewknob the
 * knob itself shows both views by picture and name (see ViewGlyph).
 *
 * The one thing the card must say plainly: the views are the SAME BOOKS.
 * Switching changes layout and language, never data and never features —
 * the old copy ("only what you need", "all features") said hiding, which is
 * exactly what the product does not do (O5: nothing lost, only placed).
 *
 * @since wv-5 (2026-08-06)
 */
import { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { toast } from '@/shared/ui/toast';
import { useExperienceLevel } from '../hooks/useExperienceLevel';
import { EXPERIENCE_LEVELS, getLevelMeta, type ExperienceLevel } from '../config/experienceLevel';

/**
 * viewknob (founder, 27 Sep 2026): "no one will be able to tell which view is
 * in, and no one will understand what this icon means". The old knob showed a
 * bar chart (reads as Reports) and a swap arrow (reads as Transfers), and only
 * ever the view you were already in. Now the knob draws BOTH screens as tiny
 * wireframes, each with its name under it, and a teal thumb sits behind the
 * one you're on. A picture of a screen says "this changes the screen" before
 * anybody presses it. Still h-8: the header must not grow.
 */
function ViewGlyph({ id, size = 16 }: { id: ExperienceLevel; size?: number }) {
    const h = Math.round(size * 0.6875);
    return id === 'expert' ? (
        // Books: a sidebar and a dense ledger.
        <svg width={size} height={h} viewBox="0 0 16 11" fill="none" aria-hidden="true">
            <rect x="0.6" y="0.6" width="14.8" height="9.8" rx="1.6" stroke="currentColor" strokeWidth="1.2" />
            <path d="M4.6 0.6v9.8" stroke="currentColor" strokeWidth="1.2" />
            <path d="M6.6 3.3h6.8M6.6 5.5h6.8M6.6 7.7h6.8" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
        </svg>
    ) : (
        // Flow: a few big cards, one after another.
        <svg width={size} height={h} viewBox="0 0 16 11" fill="none" aria-hidden="true">
            <rect x="0.6" y="0.6" width="14.8" height="9.8" rx="1.6" stroke="currentColor" strokeWidth="1.2" />
            <rect x="3" y="2.6" width="10" height="2.4" rx="0.9" fill="currentColor" />
            <rect x="3" y="6" width="10" height="2.4" rx="0.9" fill="currentColor" />
        </svg>
    );
}

/**
 * Each view wears its own colour (founder, 27 Sep: "a little colorful"): Flow
 * is Karr's teal, Books is ledger ink. After a few switches the colour alone
 * says where you are. Not amber (Karr's warning colour) and not red.
 */
const TONE: Record<string, { thumb: string; idle: string; tile: string }> = {
    business_owner: {
        thumb: 'from-emerald-400 to-teal-600 shadow-teal-700/30',
        idle: 'text-teal-600/70 dark:text-teal-400/70',
        tile: 'from-emerald-400 to-teal-600 shadow-teal-600/30',
    },
    expert: {
        thumb: 'from-sky-500 to-indigo-600 shadow-indigo-700/30',
        idle: 'text-indigo-500/70 dark:text-indigo-300/70',
        tile: 'from-sky-500 to-indigo-600 shadow-indigo-600/30',
    },
};
const toneOf = (id: string) => TONE[id] ?? TONE.business_owner;

type TurnDoc = Document & {
    startViewTransition?: (cb: () => void | Promise<void>) => { ready: Promise<void>; finished: Promise<void> };
};

/** Two frames: the new route has painted before the page finishes turning. */
const painted = () => new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));

/**
 * The page turns over like a card (globals.css, viewknob): the View Transitions
 * snapshots rotate, the live page never does. No support, or less motion asked
 * for, and the change is simply instant. `then` is a navigation that belongs to
 * the same turn: the back of the card is the page it lands on, not the old one.
 */
function turnPage(forward: boolean, apply: () => void, then?: () => Promise<unknown>) {
    const doc = document as TurnDoc;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!doc.startViewTransition || still) { apply(); void then?.(); return; }
    const root = document.documentElement;
    root.dataset.viewTurn = forward ? 'fwd' : 'back';
    const turn = doc.startViewTransition(async () => {
        flushSync(apply);
        if (then) { await then(); await painted(); }
    });
    // A slow landing page outlasts the browser's ~4 s wait: it skips the turn
    // and rejects `ready` (and `finished`). The switch has already happened, so
    // a skipped animation is nothing to report (two crashes a day on /dashboard).
    turn.ready.catch(() => {});
    turn.finished.catch(() => {}).finally(() => { delete root.dataset.viewTurn; });
}

export function ViewSwitch() {
    const { level, setLevel } = useExperienceLevel();
    const [open, setOpen] = useState(false);
    const closeTimer = useRef<number | null>(null);
    const meta = getLevelMeta(level);
    const other = EXPERIENCE_LEVELS.find(l => l.id !== meta.id) ?? EXPERIENCE_LEVELS[0];
    const onBooks = meta.id === 'expert';

    useEffect(() => () => { if (closeTimer.current) window.clearTimeout(closeTimer.current); }, []);

    const show = () => {
        if (closeTimer.current) window.clearTimeout(closeTimer.current);
        setOpen(true);
    };
    // A short grace period so the pointer can travel pill → card without the
    // card vanishing underneath it.
    const hide = () => {
        if (closeTimer.current) window.clearTimeout(closeTimer.current);
        closeTimer.current = window.setTimeout(() => setOpen(false), 150);
    };

    // Two states are a switch, not a menu. The toast names where you landed,
    // says nothing was lost, and offers the way back: trying it must be safe.
    //
    // flowhome (founder, 27 Sep 2026): Flow is its own screen, so turning to
    // Flow lands on the Flow home, wherever you were in Books. Turning to Books
    // stays on the page you're on. "Back to Books" returns to the exact page
    // you left, so trying Flow never loses your place.
    const navigate = useNavigate();
    const turnTo = (id: ExperienceLevel, href?: string) => turnPage(
        id === 'expert',
        () => setLevel(id),
        href ? () => navigate({ href }) : undefined,
    );

    const go = (id: ExperienceLevel) => {
        if (id === meta.id) return;
        const from = meta;
        const target = getLevelMeta(id);
        const here = window.location.pathname + window.location.search + window.location.hash;
        const toFlowHome = id === 'business_owner' && window.location.pathname !== '/dashboard';
        turnTo(id, toFlowHome ? '/dashboard' : undefined);
        toast.success(`You're in ${target.label} view`, {
            description: `Same books, a different screen. ${target.description}.`,
            action: { label: `Back to ${from.label}`, onClick: () => turnTo(from.id, toFlowHome ? here : undefined) },
        });
    };

    return (
        <div className="relative" onMouseEnter={show} onMouseLeave={hide}>
            {/* viewknob: both screens, each named in tiny type under its
                picture; the teal thumb slides behind the one you're on. h-8
                like every header control, fixed width, so the header never
                grows or jumps (wv-8, wv-12). Pressing anywhere flips. */}
            <button
                type="button"
                onClick={() => go(other.id)}
                onFocus={show}
                onBlur={hide}
                aria-label={`You're in the ${meta.label} view. Switch to ${other.label}`}
                className="relative grid grid-cols-2 w-[60px] h-8 p-0.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-100 dark:bg-stone-800 hover:border-teal-400 dark:hover:border-teal-600 transition-colors"
            >
                <span
                    aria-hidden="true"
                    className={`absolute top-0.5 bottom-0.5 left-0.5 w-[calc(50%-2px)] rounded-md bg-gradient-to-br ${toneOf(meta.id).thumb} shadow-sm transition-[transform,background-color] duration-300 ease-out ${onBooks ? 'translate-x-full' : 'translate-x-0'}`}
                />
                {EXPERIENCE_LEVELS.map(lvl => {
                    const here = lvl.id === meta.id;
                    return (
                        <span
                            key={lvl.id}
                            aria-hidden="true"
                            className={`relative flex flex-col items-center justify-center gap-[2px] transition-colors duration-300 ${here ? 'text-white' : `${toneOf(lvl.id).idle}`}`}
                        >
                            <ViewGlyph id={lvl.id} size={14} />
                            <span className="text-[7px] leading-none font-semibold tracking-wide">{lvl.label}</span>
                        </span>
                    );
                })}
            </button>

            {/* The explainer card — which view you're in, what actually differs,
                and a real switch: each row is clickable (wv-8, founder: premium,
                in Karr's own language — teal + stone, serif names, quiet depth). */}
            {open && (
                <div className="absolute top-full right-0 mt-2 w-80 rounded-2xl border border-stone-200/80 dark:border-stone-700/80 bg-white/95 dark:bg-stone-900/95 backdrop-blur-xl shadow-2xl shadow-stone-900/10 dark:shadow-black/40 p-2 z-[60] animate-in fade-in zoom-in-95 slide-in-from-top-1 duration-200">
                    <div className="space-y-1.5">
                        {EXPERIENCE_LEVELS.map(lvl => {
                            const isActive = lvl.id === meta.id;
                            return (
                                <button
                                    key={lvl.id}
                                    type="button"
                                    onClick={() => go(lvl.id)}
                                    aria-pressed={isActive}
                                    className={`group w-full flex items-start gap-3 rounded-xl p-3 text-left transition-all duration-150 ${isActive
                                        ? 'bg-gradient-to-br from-teal-50 to-teal-100/60 dark:from-teal-950/40 dark:to-teal-900/20 ring-1 ring-teal-200/70 dark:ring-teal-800/50'
                                        : 'hover:bg-stone-50 dark:hover:bg-stone-800/60'}`}
                                >
                                    <span className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 transition-colors ${isActive
                                        ? `bg-gradient-to-br ${toneOf(lvl.id).tile} text-white shadow-sm`
                                        : 'bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 group-hover:bg-teal-50 dark:group-hover:bg-teal-950/30 group-hover:text-teal-700 dark:group-hover:text-teal-400'}`}>
                                        <ViewGlyph id={lvl.id} size={18} />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="flex items-center gap-2">
                                            <span className={`font-serif text-sm font-semibold ${isActive ? 'text-teal-900 dark:text-teal-200' : 'text-stone-800 dark:text-stone-100'}`}>
                                                {lvl.label}
                                            </span>
                                            {isActive ? (
                                                <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-teal-700 dark:text-teal-300 bg-white/80 dark:bg-teal-950/50 border border-teal-200/70 dark:border-teal-800/50 rounded-full px-1.5 py-0.5">
                                                    <Check size={9} strokeWidth={3} /> You're here
                                                </span>
                                            ) : (
                                                <span className="text-[10px] font-medium text-stone-300 dark:text-stone-600 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                                                    Switch
                                                </span>
                                            )}
                                        </span>
                                        <span className="block text-[11px] text-stone-500 dark:text-stone-400 leading-snug mt-0.5">
                                            {lvl.description}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <p className="px-2.5 pt-2.5 pb-1 mt-1.5 text-[11px] text-stone-400 dark:text-stone-500 leading-snug border-t border-stone-100 dark:border-stone-800">
                        Same books either way — switching changes the layout and the
                        language, never your data.
                    </p>
                </div>
            )}
        </div>
    );
}
